<script setup lang="ts">
/**
 * RecentIssuesCard — shows the 10 most-recently-updated issues.
 *
 * D.2 CLOSED: Previously used `listIssues().slice(0,6)` (magic hardcoded slice).
 * Now calls GET /v1/issues?limit=10 via useIssues() composable — real cursor-
 * paginated endpoint, no magic slice. See docs/superpowers/risks.md §D.2.
 */
import { ref, computed } from 'vue';
import { RouterLink } from 'vue-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import { useIssues } from '@/composables/useIssues';
import { useIssuesStore } from '@/stores/issues';
import { SEVERITY_BADGE, SEVERITY_LABEL } from '@/components/issues/issue-enums';

dayjs.extend(relativeTime);

// Static filter ref — recent 10 issues, no filter, sorted by updatedAt DESC.
const filters = ref({ tab: 'all' as const, limit: 10 });
const { isLoading, listIds } = useIssues(filters);
const store = useIssuesStore();

const issues = computed(() =>
  listIds.value
    .map((id: string) => store.entitiesById.get(id))
    .filter((i): i is NonNullable<ReturnType<typeof store.entitiesById.get>> => i != null),
);
</script>

<template>
  <BaseCard padding="md" data-testid="recent-issues-card">
    <template #header>
      <h2 class="text-section text-fg">Recent issues</h2>
    </template>

    <div v-if="isLoading" class="flex flex-col gap-3">
      <div v-for="i in 4" :key="i" class="h-8 bg-canvas-soft rounded animate-pulse" />
    </div>

    <ul
      v-else-if="issues.length > 0"
      class="divide-y divide-border"
      data-testid="issues-list"
    >
      <li
        v-for="issue in issues"
        :key="issue.id"
        class="py-3 flex items-center gap-3 min-w-0"
      >
        <BaseBadge :variant="SEVERITY_BADGE[issue.severity] ?? 'info'" class="flex-shrink-0">
          {{ SEVERITY_LABEL[issue.severity] ?? String(issue.severity) }}
        </BaseBadge>
        <RouterLink
          :to="`/issues/${issue.id}`"
          class="flex-1 min-w-0 no-underline hover:underline"
        >
          <span class="text-body text-fg block truncate">{{ issue.title }}</span>
          <span v-if="issue.location" class="text-caption text-muted block truncate">
            {{ issue.location }}
          </span>
        </RouterLink>
        <span class="text-caption text-muted flex-shrink-0 font-mono tabular-nums">
          {{ dayjs(issue.updatedAt).fromNow() }}
        </span>
      </li>
    </ul>

    <p v-else class="text-body text-muted">No issues yet.</p>
  </BaseCard>
</template>
